/**
 * PlayerSlot — per-file-type viewer router.
 *
 * Maps a GetFileResponse to the correct React viewer component using
 * resolveViewerType(). For video/audio, declaratively renders the
 * <video>/<audio> element (with <source> children) in JSX and hands a
 * stable useId()-generated id to KollabPlayer via mediaElementId.
 *
 * Handles per-type routing for the multi-column player world.
 *
 * aria-controls approach: id-only.
 *   - We generate a stable id via React.useId() and pass it to both the
 *     <video>/<audio> element and to KollabPlayer as mediaElementId.
 *   - We do NOT pass mediaElementRef to KollabPlayer so we stay on the
 *     "exactly one of id/ref" happy path (no warning).
 *   - A local mediaRef is still kept for useAutoResume, which needs to
 *     snapshot the paused state synchronously during render.
 */

import React, {useEffect, useId, useRef, useState} from 'react';
import KollabPlayer from '../player/KollabPlayer';
import KollabImageViewer from '../image-viewer/KollabImageViewer';
import KollabDocumentViewer from '../document-viewer/KollabDocumentViewer';
import KollabTextViewer from '../text-viewer/KollabTextViewer';
import KollabCommentStream from '../comment-stream/KollabCommentStream';
import KollabDownloadOverlay from '../download-overlay/KollabDownloadOverlay';
import SyncDebugOverlay from './SyncDebugOverlay';
import { resolveViewerType } from './resolveViewerType';
import { imageProxies, imageProxy, isBrowserRenderableImage, pdfProxy } from './proxySelection';
import type { ImageSource } from '../image-viewer/types';
import { useAutoResume } from './useAutoResume';
import type { PlayerSlotProps } from './types';
import type { GetFileResponse } from '../player-loader/loadFile';

// Ref API types for the viewers. We use `React.ElementRef<typeof X>` so we
// don't have to import the concrete API interfaces from each viewer's types
// module — those modules may transitively fail to compile under working-tree
// edits, and we only need the structural ref shape here.
type KollabPlayerRefApi = React.ElementRef<typeof KollabPlayer>;
type KollabImageViewerRefApi = React.ElementRef<typeof KollabImageViewer>;
type KollabDocumentViewerRefApi = React.ElementRef<typeof KollabDocumentViewer>;
type KollabTextViewerRefApi = React.ElementRef<typeof KollabTextViewer>;

// ── Helpers ──────────────────────────────────────────────────────────────

function getLinkId(): string | null {
  const w = window as any;
  if (w.jsconnect?.link?.id) return String(w.jsconnect.link.id);
  return null;
}

function getProjectPerms(): { canDownload: boolean; canShare: boolean; isAdmin: boolean } {
  const w = window as any;
  const project = w.jsconnect?.project ?? {};
  return {
    canDownload: !!project.can_download,
    canShare: !!project.can_share,
    isAdmin: !!project.is_admin,
  };
}

function buildDownloadUrl(fileId: number): string | null {
  const linkId = getLinkId();
  return `download?id=${fileId}` + (linkId ? `&link=${encodeURIComponent(linkId)}` : '');
}

function widthOrDefault(data: GetFileResponse): number {
  return data.metadata.width > 0 ? data.metadata.width : 960;
}

function heightOrDefault(data: GetFileResponse): number {
  return data.metadata.height > 0 ? data.metadata.height : 540;
}

function fileTypeName(data: GetFileResponse): string {
  switch (data.file_type_id) {
    case 1: return 'Video';
    case 2: return 'Audio';
    case 3: return 'Image';
    case 5: return 'Comment Stream';
    case 6: return 'Document';
    default: {
      const ext = data.file_extension || '';
      return ext ? `${ext.toUpperCase()} File` : 'File';
    }
  }
}

/**
 * URL the document viewer should load. Documents such as .docx aren't
 * renderable by pdf.js themselves, but get a PDF proxy generated for them, so
 * prefer that proxy whenever the original isn't already a PDF. Also covers the
 * case where the server withholds original_file_url (it returns null when
 * proxies exist and the user can't download).
 */
function documentUrl(data: GetFileResponse): string {
  if (data.mime_type === 'application/pdf') {
    return data.original_file_url ?? '';
  }
  return pdfProxy(data)?.url ?? data.original_file_url ?? '';
}

/**
 * URL the image viewer should load. Prefer the image proxy whenever one
 * exists — originals such as .psd/.tif/.exr can't be decoded by the browser at
 * all, and even for renderable originals the proxy is the lighter, already
 * web-optimised copy the server generated for viewing. Falls back to the
 * original when there's no image proxy.
 */
function imageUrl(data: GetFileResponse): string {
  return imageProxy(data)?.url ?? data.original_file_url ?? '';
}

/** "1600x1200 JPG" when dimensions are known, otherwise just "JPG". */
function renditionLabel(width: number, height: number, ext: string, qualifier: string): string {
  const name = (ext || '').toUpperCase();
  const dims = width > 0 && height > 0 ? `${width}x${height}` : '';
  const prefix = [dims, name].filter(Boolean).join(' ');
  return prefix ? `${prefix} (${qualifier})` : qualifier;
}

/**
 * Selectable renditions for the image viewer's settings menu: every image
 * proxy in server-preference order, then the original.
 *
 * The original is only offered when the user may download it and the browser
 * can actually decode it — a PSD/TIFF/EXR original would be a guaranteed
 * broken view. Note `original_file_url` is already null when proxies exist and
 * the user lacks download permission, so the canDownload check is a second
 * line of defence rather than the only one.
 */
function imageSourcesFor(data: GetFileResponse, canDownload: boolean): ImageSource[] {
  const sources: ImageSource[] = imageProxies(data).map((p) => ({
    url: p.url,
    label: renditionLabel(p.width, p.height, p.file_extension, 'Proxy'),
  }));

  if (data.original_file_url && canDownload && isBrowserRenderableImage(data.mime_type)) {
    sources.push({
      url: data.original_file_url,
      label: renditionLabel(data.metadata.width, data.metadata.height, data.file_extension, 'Original'),
    });
  }

  // Nothing matched (e.g. a non-image proxy only) — fall back to whatever the
  // viewer would have loaded before there was a menu.
  if (sources.length === 0) {
    sources.push({ url: imageUrl(data), label: 'Original' });
  }

  return sources;
}

function mimeWithCodec(mimeType: string, mimeCodec: string | null | undefined): string {
  return mimeCodec ? `${mimeType}; codecs="${mimeCodec}"` : mimeType;
}

// ── Global callbacks wired into KollabPlayer ─────────────────────────────
//
// These bridge the React player into page-level global helpers
// (keyboard shortcuts, comments, annotation editor). They are defined at
// module level so their references stay stable across re-renders, and each
// call is guarded with a typeof check so jest/SSR/link pages don't blow up
// when the globals aren't present.

/**
 * Pre-seek guard: if an annotation editor is currently mounted and has
 * unsaved changes, prompt the user to discard them. Returning false
 * cancels the seek (KollabPlayer honors the boolean).
 */
function legacyOnBeforeSeek(): boolean {
  const w = window as any;
  const h = w.annotationEditorHandle;
  if (h && h.api && h.api.isDirty && h.api.isDirty()) {
    if (window.confirm('You have unsaved annotation changes. Discard them?')) {
      try { h.unmount(); } catch (_) {}
      w.annotationEditorHandle = null;
      return true;
    }
    return false;
  }
  return true;
}

/**
 * onMounted: initialise the page-level keyboard-shortcut handler once the
 * player is mounted. Invokes the globals `setupKeyboardShortcuts()` and
 * `setupHoldLabel()` when they are present on the page.
 */
function legacyOnPlayerMounted(): void {
  const w = window as any;
  if (typeof w.setupKeyboardShortcuts === 'function') {
    try { w.setupKeyboardShortcuts(); } catch (_) {}
  }
  if (typeof w.setupHoldLabel === 'function') {
    try { w.setupHoldLabel(); } catch (_) {}
  }
}

/**
 * onPlay: when the user starts playback, release any "held" state on the
 * comment field (the global `unhold()` helper in comments.js). Guarded to
 * avoid firing when the comment field has pending content.
 */
function legacyOnPlay(): void {
  const w = window as any;
  const commentBox = document.getElementById('comment-field') as HTMLTextAreaElement | null;
  if (commentBox && typeof commentBox.value === 'string' && commentBox.value.trim().length === 0) {
    if (typeof w.unhold === 'function') {
      try { w.unhold(); } catch (_) {}
    }
  }
}

// ── Component ────────────────────────────────────────────────────────────

// Stage style for the image viewer. Uses an explicit height rather than
// aspectRatio so images aren't forced into 16:9. The image viewer
// handles fit-to-container sizing internally via its zoom system.
const IMAGE_STAGE_STYLE: React.CSSProperties = {
  width: '100%',
  height: '80vh',
  maxHeight: '80vh',
  minHeight: 240,
  display: 'flex',
  overflow: 'hidden',
};

// Stage style for document, text, comment-stream, and download viewers.
// No aspect-ratio constraint — these viewers benefit from more vertical
// space, especially when comparing side-by-side. Uses a fixed 80vh height
// so they fill most of the viewport.
const DOC_TEXT_STAGE_STYLE: React.CSSProperties = {
  width: '100%',
  height: '80vh',
  maxHeight: '80vh',
  minHeight: 240,
  display: 'flex',
  overflow: 'hidden',
};

export function PlayerSlot({ fileData, colId, isActive = true, onActivate, onMediaEnded, autoplay = true, onViewerReady, syncedSession, onMediaReady }: PlayerSlotProps) {
  const syncedSessionManager = typeof window !== 'undefined' ? window.kollabSyncedSession?.manager : undefined;
  const mediaRef = useRef<HTMLMediaElement | null>(null);
  const stageRef = useRef<HTMLElement | null>(null);
  const kollabPlayerApiRef = useRef<KollabPlayerRefApi | null>(null);
  const imageViewerRef = useRef<KollabImageViewerRefApi | null>(null);
  const documentViewerRef = useRef<KollabDocumentViewerRefApi | null>(null);
  const textViewerRef = useRef<KollabTextViewerRefApi | null>(null);
  const mediaElementId = useId();
  const imageElementId = useId();
  const stageId = useId();
  const viewerType = resolveViewerType(fileData);

  // Debug stats overlay — enabled by setting localStorage key
  // "debug.player.playbackStats" to "true". Hidden developer option,
  // not exposed in the UI.
  const [showDebugStats, setShowDebugStats] = useState(() => {
    try { return localStorage.getItem('debug.player.playbackStats') === 'true'; } catch { return false; }
  });
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'debug.player.playbackStats') {
        setShowDebugStats(e.newValue === 'true');
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Which image rendition (proxy vs original) is loaded into the <img>.
  // PlayerSlot owns this because it also owns the <img>'s src: the viewer
  // mutating src imperatively would be silently reverted the next time React
  // reconciled that attribute. Reset per file, like the video player does.
  const [activeImageSourceIndex, setActiveImageSourceIndex] = useState(0);
  useEffect(() => { setActiveImageSourceIndex(0); }, [fileData.id]);

  // Snapshot playback state across file changes for the video/audio branch.
  // Harmless no-op for other viewer types since the ref stays null.
  useAutoResume(mediaRef, fileData.id);

  // Fire onViewerReady after mount / after the ref has been populated.
  // Keyed on viewerType + fileData.id so it re-fires when the column
  // swaps between viewer types (e.g. a version switch that changes the
  // file type) or between files of the same type.
  useEffect(() => {
    if (!onViewerReady) return;
    // download_only overrides the normal viewer type — always report 'download'.
    if (fileData.download_only) {
      onViewerReady({ type: 'download', api: null });
      return;
    }
    switch (viewerType) {
      case 'video':
      case 'audio':
        onViewerReady({ type: viewerType, api: kollabPlayerApiRef.current });
        break;
      case 'image':
        onViewerReady({ type: 'image', api: imageViewerRef.current });
        break;
      case 'document':
        onViewerReady({ type: 'document', api: documentViewerRef.current });
        break;
      case 'text':
        onViewerReady({ type: 'text', api: textViewerRef.current });
        break;
      case 'commentstream':
        onViewerReady({ type: 'commentstream', api: null });
        break;
      case 'download':
      default:
        onViewerReady({ type: 'download', api: null });
        break;
    }
    // `onViewerReady` is intentionally excluded from the dep array — the
    // manager wraps it once per mount in `createDefaultMounter`, so the
    // reference is stable for the life of the column, and adding it would
    // force the effect to re-run on every parent rerender and potentially
    // re-fire with a now-stale ref during an unrelated prop change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewerType, fileData.id, fileData.download_only]);

  // Download-only override: when the server flags a file as download-only,
  // short-circuit the normal viewer routing and show a download overlay
  // regardless of the file's actual type.
  if (fileData.download_only) {
    const perms = getProjectPerms();
    const downloadUrl = perms.canDownload ? buildDownloadUrl(fileData.id) : null;
    const linkId = getLinkId();
    return (
      <div className="player-stage" style={DOC_TEXT_STAGE_STYLE}>
        <KollabDownloadOverlay
          fileTitle={fileData.title}
          fileSize={fileData.size}
          fileTypeName={fileTypeName(fileData)}
          {...(linkId ? { linkId } : {})}
          iconUrl="img/Unknown_Doc.svg"
          downloadUrl={downloadUrl}
          preventDownload={!perms.canDownload}
          preventDownloadMessage="This file has been set to download only."
          width={widthOrDefault(fileData)}
          height={heightOrDefault(fileData)}
        />
      </div>
    );
  }

  switch (viewerType) {
    case 'video':
    case 'audio': {
      const linkId = getLinkId();
      const perms = getProjectPerms();
      const downloadUrl = buildDownloadUrl(fileData.id);
      const isVideo = viewerType === 'video';
      const poster = isVideo ? fileData.thumbnails.poster_url ?? undefined : undefined;

      // Build <source> children (proxies first, then original).
      const sources: React.ReactNode[] = [];
      for (const proxy of fileData.proxies) {
        sources.push(
          <source
            key={`p-${proxy.url}`}
            src={proxy.url}
            type={mimeWithCodec(proxy.mime_type, proxy.mime_codec)}
          />,
        );
      }
      if (fileData.original_file_url) {
        // mime_codec is only populated for video files; audio uses plain mime_type.
        const type = isVideo
          ? mimeWithCodec(fileData.mime_type, fileData.mime_codec)
          : fileData.mime_type;
        sources.push(
          <source
            key={`o-${fileData.original_file_url}`}
            src={fileData.original_file_url}
            type={type}
          />,
        );
      }

      // Set width/height attributes and aspect-ratio so the browser
      // reserves the correct space before metadata loads. Without this,
      // the video starts at 0 height and jumps when loadedmetadata fires.
      const videoWidth = widthOrDefault(fileData);
      const videoHeight = heightOrDefault(fileData);

      const mediaElement = isVideo ? (
        <video
          id={mediaElementId}
          ref={mediaRef as React.Ref<HTMLVideoElement>}
          referrerPolicy="no-referrer-when-downgrade"
          crossOrigin="use-credentials"
          tabIndex={2}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          {...({ 'x-webkit-airplay': 'allow' } as any)}
          preload="auto"
          poster={poster}
          controlsList="nodownload"
          controls
          playsInline
          width={videoWidth}
          height={videoHeight}
          style={{ aspectRatio: `${videoWidth} / ${videoHeight}`, width: '100%', height: 'auto' }}
        >
          {sources}
        </video>
      ) : (
        <audio
          id={mediaElementId}
          ref={mediaRef as React.Ref<HTMLAudioElement>}
          crossOrigin="use-credentials"
          tabIndex={2}
          preload="auto"
          controlsList="nodownload"
          controls
        >
          {sources}
        </audio>
      );

      return (
        <div
          id={stageId}
          tabIndex={1}
          ref={stageRef as React.Ref<HTMLDivElement>}
          className="player-stage"
          style={isVideo ? { '--video-ar-num': String(videoWidth / videoHeight) } as React.CSSProperties : undefined}
          onClick={onActivate}
        >
          {mediaElement}
          <KollabPlayer
            ref={kollabPlayerApiRef}
            mediaElementId={mediaElementId}
            fullscreenTargetRef={stageRef as React.RefObject<HTMLElement | null>}
            mediaType={isVideo ? 'video' : 'audio'}
            fileId={fileData.id}
            fps={fileData.timing.fps}
            startFrame={fileData.timing.start_frame}
            dropFrame={fileData.timing.drop_frame}
            canDownload={perms.canDownload}
            fileTitle={fileData.title}
            fileTypeName={isVideo ? 'Video' : 'Audio'}
            fileSize={fileData.size}
            waveformColor="#1f2c40"
            isActive={isActive}
            {...(isActive ? { sidePanelElementId: 'comment-tabs' } : {})}
            sidePanelWidth={400}
            sidePanelTooltip="Show / hide comments"
            autoplay={autoplay}
            {...(perms.canDownload && downloadUrl ? { downloadUrl } : {})}
            {...(linkId ? { linkId } : {})}
            {...(!isVideo && fileData.thumbnails.waveform_url
              ? { waveformUrl: fileData.thumbnails.waveform_url }
              : {})}
            {...(fileData.thumbnails.thumbnail_hoverscrub_url
              ? { thumbnailSpriteUrl: fileData.thumbnails.thumbnail_hoverscrub_url }
              : {})}
            onBeforeSeek={legacyOnBeforeSeek}
            onMounted={legacyOnPlayerMounted}
            onPlay={legacyOnPlay}
            onEnded={onMediaEnded}
            {...(onMediaReady ? { onMediaReady } : {})}
            {...(syncedSession ? { syncedSession } : {})}
            {...(syncedSessionManager ? { syncedSessionManager } : {})}
          />
          <SyncDebugOverlay
            visible={showDebugStats}
            mediaRef={mediaRef as React.RefObject<HTMLMediaElement | null>}
            role={isActive ? 'source' : 'follower'}
            colId={colId}
          />
        </div>
      );
    }

    case 'image': {
      const linkId = getLinkId();
      const perms = getProjectPerms();
      const downloadUrl = buildDownloadUrl(fileData.id);
      const imageSources = imageSourcesFor(fileData, perms.canDownload);
      const url = (imageSources[activeImageSourceIndex] ?? imageSources[0]).url;

      return (
        <div
          id={stageId}
          ref={stageRef as React.Ref<HTMLDivElement>}
          className="player-stage"
          style={IMAGE_STAGE_STYLE}
          onClick={onActivate}
        >
          <img
            id={imageElementId}
            draggable={false}
            crossOrigin="use-credentials"
            style={{ display: 'none' }}
            src={url}
            alt={fileData.title}
          />
          <KollabImageViewer
            ref={imageViewerRef}
            imageElementId={imageElementId}
            isActive={isActive}
            {...(isActive ? { sidePanelElementId: 'comment-tabs' } : {})}
            sidePanelWidth={400}
            sidePanelTooltip="Show / hide comments"
            {...(linkId ? { linkId } : {})}
            canDownload={perms.canDownload}
            fileTitle={fileData.title}
            fileTypeName="Image"
            fileSize={fileData.size}
            {...(perms.canDownload && downloadUrl ? { downloadUrl } : {})}
            imageSources={imageSources}
            activeSourceIndex={activeImageSourceIndex}
            onSourceChange={setActiveImageSourceIndex}
            onMounted={legacyOnPlayerMounted}
          />
        </div>
      );
    }

    case 'document': {
      const perms = getProjectPerms();
      const downloadUrl = perms.canDownload ? buildDownloadUrl(fileData.id) : null;
      const linkId = getLinkId();
      return (
        <div className="player-stage" style={DOC_TEXT_STAGE_STYLE} onClick={onActivate}>
          <KollabDocumentViewer
            ref={documentViewerRef}
            fileUrl={documentUrl(fileData)}
            fileId={fileData.id}
            fileTitle={fileData.title}
            width={widthOrDefault(fileData)}
            height={heightOrDefault(fileData)}
            {...(linkId ? { linkId } : {})}
            canDownload={perms.canDownload}
            downloadUrl={downloadUrl}
            fileTypeName={fileTypeName(fileData)}
            fileSize={fileData.size}
            {...(isActive ? { sidePanelElementId: 'comment-tabs' } : {})}
            sidePanelWidth={400}
            sidePanelTooltip="Show / hide comments"
          />
        </div>
      );
    }

    case 'text': {
      const perms = getProjectPerms();
      const downloadUrl = perms.canDownload ? buildDownloadUrl(fileData.id) : null;
      const linkId = getLinkId();
      return (
        <div className="player-stage" style={DOC_TEXT_STAGE_STYLE} onClick={onActivate}>
          <KollabTextViewer
            ref={textViewerRef}
            fileUrl={fileData.original_file_url ?? ''}
            fileTitle={fileData.title}
            fileSize={fileData.size}
            width={widthOrDefault(fileData)}
            height={heightOrDefault(fileData)}
            {...(linkId ? { linkId } : {})}
            downloadUrl={downloadUrl}
            fileTypeName="Text File"
            {...(isActive ? { sidePanelElementId: 'comment-tabs' } : {})}
            sidePanelWidth={400}
            sidePanelTooltip="Show / hide comments"
          />
        </div>
      );
    }

    case 'commentstream': {
      return (
        <div className="player-stage" style={DOC_TEXT_STAGE_STYLE} onClick={onActivate}>
          <KollabCommentStream fileTitle={fileData.title} />
        </div>
      );
    }

    case 'download':
    default: {
      // The download viewer fills the whole column's stage. PlayerColumn
      // binds the activation click on the column wrapper itself for this
      // viewer type (the user explicitly requested this exception, since
      // there's no other selectable element inside).
      const perms = getProjectPerms();
      const downloadUrl = perms.canDownload ? buildDownloadUrl(fileData.id) : null;
      const linkId = getLinkId();
      return (
        <div className="player-stage" style={DOC_TEXT_STAGE_STYLE}>
          <KollabDownloadOverlay
            fileTitle={fileData.title}
            fileSize={fileData.size}
            fileTypeName={fileTypeName(fileData)}
            {...(linkId ? { linkId } : {})}
            iconUrl="img/Unknown_Doc.svg"
            downloadUrl={downloadUrl}
            preventDownload={!perms.canDownload}
            width={widthOrDefault(fileData)}
            height={heightOrDefault(fileData)}
          />
        </div>
      );
    }
  }
}

export default PlayerSlot;
